import React, { useEffect, useMemo, useState } from "react";
import api from "../../api/client";
import UploadImage from "../common/UploadImage";

const asList = (payload) =>
  Array.isArray(payload) ? payload : payload?.events || payload?.data || [];

function formatDate(value) {
  const d = new Date(value);
  return Number.isNaN(d.getTime())
    ? "Date to be confirmed"
    : d.toLocaleDateString(undefined, {
        weekday: "long", day: "numeric", month: "long", year: "numeric",
      });
}

export default function EventsPage() {
  const [events, setEvents] = useState([]);
  const [status, setStatus] = useState("loading");
  const [error, setError] = useState("");
  const [scope, setScope] = useState("upcoming");
  const [search, setSearch] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setStatus("loading");
      try {
        const res = await api.get("/events/getallevents");
        if (cancelled) return;
        setEvents(asList(res.data));
        setStatus("ready");
      } catch (err) {
        if (cancelled) return;
        setError(err.response?.data?.message || "We couldn't load the events list.");
        setStatus("error");
      }
    })();

    return () => { cancelled = true; };
  }, []);

  const { upcoming, past } = useMemo(() => {
    const now = Date.now();
    const up = [], back = [];
    for (const e of events) {
      (new Date(e.eventDate).getTime() >= now ? up : back).push(e);
    }
    up.sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate));
    back.sort((a, b) => new Date(b.eventDate) - new Date(a.eventDate));
    return { upcoming: up, past: back };
  }, [events]);

  const visible = useMemo(() => {
    const base = scope === "upcoming" ? upcoming : past;
    const term = search.trim().toLowerCase();
    if (!term) return base;
    return base.filter(
      (e) =>
        e.nameevents?.toLowerCase().includes(term) ||
        e.description?.toLowerCase().includes(term)
    );
  }, [scope, upcoming, past, search]);

  return (
    <div className="es-page">
      <p className="es-eyebrow">What&rsquo;s on</p>
      <h1 className="es-h1">Events and workshops</h1>
      <p className="es-lede">
        Open days, family mornings and training sessions for parents and
        carers. Most are free; where a place must be booked we say so on the
        listing.
      </p>

      <div style={{
        display: "flex", gap: "1rem", flexWrap: "wrap",
        alignItems: "flex-end", marginBottom: "2rem",
      }}>
        <div role="group" aria-label="Filter events by date"
             style={{ display: "flex", gap: "0.4rem" }}>
          <button type="button"
            className={`es-btn ${scope === "upcoming" ? "es-btn--primary" : "es-btn--ghost"}`}
            aria-pressed={scope === "upcoming"}
            onClick={() => setScope("upcoming")}>
            Upcoming ({upcoming.length})
          </button>
          <button type="button"
            className={`es-btn ${scope === "past" ? "es-btn--primary" : "es-btn--ghost"}`}
            aria-pressed={scope === "past"}
            onClick={() => setScope("past")}>
            Past ({past.length})
          </button>
        </div>

        <div className="es-field" style={{ margin: 0, flex: "1 1 14rem" }}>
          <label className="es-label" htmlFor="event-search">Search events</label>
          <input id="event-search" type="search" className="es-input"
            placeholder="e.g. workshop, open day"
            value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
      </div>

      {status === "loading" && (
        <div className="es-grid" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="es-card">
              <div className="es-skeleton es-skeleton--card" />
              <div className="es-card__body">
                <div className="es-skeleton es-skeleton--text" style={{ width: "45%" }} />
                <div className="es-skeleton es-skeleton--text" style={{ width: "80%" }} />
                <div className="es-skeleton es-skeleton--text" />
              </div>
            </div>
          ))}
          <span className="es-visually-hidden">Loading events</span>
        </div>
      )}

      {status === "error" && (
        <div className="es-alert es-alert--error" role="alert">{error}</div>
      )}

      {status === "ready" && visible.length === 0 && (
        <div className="es-state">
          <p className="es-state__title">
            {search
              ? "No events match that search"
              : scope === "upcoming"
                ? "Nothing scheduled just now"
                : "No past events recorded"}
          </p>
          <p>
            {search
              ? "Try a different term, or switch between upcoming and past."
              : "New dates are added regularly — do check back."}
          </p>
        </div>
      )}

      {status === "ready" && visible.length > 0 && (
        <>
          <p className="es-muted" aria-live="polite" style={{ marginBottom: "1rem" }}>
            Showing {visible.length} {scope} event{visible.length === 1 ? "" : "s"}
          </p>

          <div className="es-grid">
            {visible.map((event) => {
              const isPast = new Date(event.eventDate).getTime() < Date.now();
              return (
                <article key={event._id} className="es-card es-card--interactive">
                  <UploadImage file={event.imageevents} alt="" className="es-media" />
                  <div className="es-card__body">
                    <span className={`es-badge ${isPast ? "es-badge--neutral" : "es-badge--ok"}`}>
                      {isPast ? "Past event" : "Upcoming"}
                    </span>
                    <h2 className="es-h3" style={{ marginTop: "0.75rem" }}>
                      {event.nameevents}
                    </h2>
                    <p className="es-muted" style={{ marginBottom: "0.75rem" }}>
                      {formatDate(event.eventDate)}
                    </p>
                    <p className="es-body" style={{ fontSize: "0.92rem" }}>
                      {event.description}
                    </p>
                  </div>
                </article>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
